import React, { useState } from 'react';
import {
    ConfigProvider,
    theme,
    Table,
    Avatar,
    Input,
    Button,
    Dropdown,
    Modal,
    DatePicker,
    Select,
    Tabs,
    Checkbox
} from 'antd';
import {
    Minus,
    Plus,
    MoreVertical,
    Paperclip,
    Send,
    Smile,
    User,
    Link2,
    History,
    Tag,
    X,
    CheckCircle2,
    Activity,
    CheckSquare,
    Edit3,
    ActivitySquare,
    ChevronDown,
} from 'lucide-react';

import Layout from '../../Layout'; // Giữ nguyên import layout của bạn
import { Link } from 'react-router-dom';
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

const { TextArea } = Input;

// ================= DỮ LIỆU MOCK =================
const USER_DB = {
    trang: { label: 'Nguyễn Thu Trang', department: '[GTV] Phòng Hành...', position: 'Giám đốc tài chính', role: '[KHOTHI] Giám đốc' },
    anh: { label: 'Mai Tuyết Anh', department: 'Phòng kinh doanh...', position: '[KHOTHI] Chuyên...', role: '[KHOTHI] Chuyên...' },
    huan: { label: 'Mr. Huấn', department: 'Phòng Kỹ thuật', position: 'Kỹ sư phần mềm', role: 'Nhân viên' }
};
const userOptions = Object.keys(USER_DB).map(key => ({ value: key, label: USER_DB[key].label }));

// Tạo DB giả lập cho Sản phẩm để auto-fill
const PRODUCT_DB = {
    'vm02': { label: 'VM02 - Bếp điện từ độ lập âm...', unit: 'Cái', price: '10000000' },
    'vpp001': { label: 'VPP001 - Bảng văn phòng', unit: 'Cái', price: '2000000' }
};
const productOptions = Object.keys(PRODUCT_DB).map(key => ({ value: key, label: PRODUCT_DB[key].label }));

export default function ContractDetail() {
    const [isDarkMode, setIsDarkMode] = useState(false);

    // ================= STATES =================
    // Modal States
    const [isAssigneeModalOpen, setIsAssigneeModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
    const [isUpdateDetailModalOpen, setIsUpdateDetailModalOpen] = useState(false);

    // Collapse States
    const [expandedBlocks, setExpandedBlocks] = useState({
        general: true,
        contractDetail: true,
        paymentTerm: true,
    });

    const toggleBlock = (key) => {
        setExpandedBlocks(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // State Thảo luận
    const [comments, setComments] = useState([
        { id: 1, author: 'Mr. Huấn', text: 'tạo HĐ', time: 'Vài giây trước', status: 'tạo HĐ' }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState('');

    const handleSendComment = () => {
        if (!inputValue.trim()) return;
        const newComment = { id: Date.now(), author: 'Người dùng', text: inputValue, time: 'Vừa xong' };
        setComments([...comments, newComment]);
        setInputValue('');
    };

    const handleDeleteComment = (id) => setComments(comments.filter(c => c.id !== id));
    const startEdit = (id, text) => { setEditingId(id); setEditValue(text); };
    const saveEdit = (id) => {
        setComments(comments.map(c => c.id === id ? { ...c, text: editValue } : c));
        setEditingId(null);
    };

    // State cho Modal "Cập nhật người phụ trách"
    const [modalAssignees, setModalAssignees] = useState([
        { id: 1, user: 'huan', department: USER_DB['huan'].department, position: USER_DB['huan'].position, role: USER_DB['huan'].role },
    ]);
    const handleAddModalAssignee = () => setModalAssignees([...modalAssignees, { id: Date.now(), user: null, department: '', position: '', role: '' }]);
    const handleRemoveModalAssignee = (id) => setModalAssignees(modalAssignees.filter(item => item.id !== id));
    const handleChangeModalUser = (id, selectedUserValue) => {
        const userInfo = USER_DB[selectedUserValue];
        setModalAssignees(modalAssignees.map(item =>
            item.id === id ? { ...item, user: selectedUserValue, department: userInfo?.department || '', position: userInfo?.position || '', role: userInfo?.role || '' } : item
        ));
    };

    // ================= XỬ LÝ MODAL SẢN PHẨM (AUTO FILL) =================
    const formatMoney = (amount) => {
        if (!amount) return '0';
        return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const [modalProducts, setModalProducts] = useState([
        { id: 1, product: 'vm02', unit: 'Cái', price: '10000000', qty: '100', discountType: '%', discountVal: '0', total: '1000000000' },
        { id: 2, product: 'vpp001', unit: 'Cái', price: '2000000', qty: '1', discountType: '%', discountVal: '0', total: '2000000' },
    ]);

    const handleAddModalProduct = () => setModalProducts([...modalProducts, { id: Date.now(), product: null, unit: '', price: '', qty: '1', discountType: '%', discountVal: '0', total: '0' }]);
    const handleRemoveModalProduct = (id) => setModalProducts(modalProducts.filter(item => item.id !== id));

    // Hàm tự động điền khi chọn sản phẩm
    const handleChangeModalProduct = (id, selectedProductValue) => {
        const productInfo = PRODUCT_DB[selectedProductValue];

        setModalProducts(prevProducts => prevProducts.map(item => {
            if (item.id === id) {
                const newPrice = productInfo?.price || '0';
                const newTotal = (parseFloat(newPrice) * parseFloat(item.qty || 1)).toString();
                return {
                    ...item,
                    product: selectedProductValue,
                    unit: productInfo?.unit || '',
                    price: newPrice,
                    total: newTotal
                };
            }
            return item;
        }));
    };

    // Hàm cho phép gõ tay để sửa Số lượng, Giá mua và tự tính tổng
    const handleUpdateProductField = (id, field, value) => {
        // Loại bỏ dấu phẩy nếu người dùng nhập số tiền có phẩy
        const rawValue = value.replace(/,/g, '');

        setModalProducts(prevProducts => prevProducts.map(item => {
            if (item.id === id) {
                const updatedItem = { ...item, [field]: rawValue };

                // Tự động tính lại thành tiền nếu sửa giá hoặc số lượng
                if (field === 'price' || field === 'qty') {
                    const priceNum = parseFloat(updatedItem.price) || 0;
                    const qtyNum = parseFloat(updatedItem.qty) || 0;
                    updatedItem.total = (priceNum * qtyNum).toString();
                }
                return updatedItem;
            }
            return item;
        }));
    };

    // ================= MOCK DATA BẢNG =================
    const productColumns = [
        { title: 'STT', dataIndex: 'stt', key: 'stt', width: 60, align: 'center' },
        {
            title: 'Sản phẩm, dịch vụ',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            // Không set width để cột này giãn tối đa
        },
        { title: 'Đơn vị', dataIndex: 'unit', key: 'unit', width: 100, className: 'whitespace-nowrap' },
        { title: 'Giá', dataIndex: 'price', key: 'price', align: 'right', width: 150, className: 'whitespace-nowrap' },
        { title: 'Số lượng', dataIndex: 'qty', key: 'qty', align: 'right', width: 110, className: 'whitespace-nowrap' },
        { title: 'Thành tiền', dataIndex: 'total', key: 'total', align: 'right', width: 180, className: 'font-medium whitespace-nowrap' },
    ];
    const productData = [
        { key: '1', stt: '1', name: 'VM02 - Bếp điện từ độ lập âm Blue... \n Từ nhà (khóa) VM02 - 700mm x 410mm', unit: 'Cái', price: '10.000.000 ₫', qty: '100', total: '1.000.000.000 ₫' },
        { key: '2', stt: '2', name: 'VPP001 - Bảng văn phòng \n Mua đại loan, độ dày 0.9mm, thép sơn...', unit: 'Cái', price: '2.000.000 ₫', qty: '1', total: '2.000.000 ₫' },
    ];

    const paymentColumns = [
        { title: 'Lần TT', dataIndex: 'times', key: 'times' },
        { title: 'Ngày thanh toán', dataIndex: 'date', key: 'date' },
        { title: 'Thanh toán từ', dataIndex: 'from', key: 'from' },
        { title: 'Số tiền', dataIndex: 'amount', key: 'amount', align: 'right' },
        { title: 'Nội dung thanh toán', dataIndex: 'note', key: 'note' },
    ];
    const paymentData = [
        { key: '1', times: 'Lần 1', date: '22/08/2026', from: '101', amount: '1.092.180.000', note: 'fg' },
    ];

    // ================= THEME & UI COMPONENTS =================
    const antdThemeConfig = {
        token: {
            colorPrimary: '#3e91ab',
            colorBgContainer: isDarkMode ? '#1f2937' : '#ffffff',
            colorText: isDarkMode ? '#f3f4f6' : '#374151',
            colorBorder: isDarkMode ? '#4b5563' : '#d1d5db',
            borderRadius: 6,
        },
        components: { Table: { headerBg: isDarkMode ? '#374151' : '#f9fafb' } },
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    };

    const CardHeader = ({ title, blockKey }) => (
        <div className="flex justify-between items-center mb-4 pb-2 border-b dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 px-4 py-2 -mx-4 -mt-4 rounded-t-lg">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100">{title}</h3>
            <div onClick={() => toggleBlock(blockKey)} className="cursor-pointer text-gray-400 hover:text-[#3e91ab] p-1">
                {expandedBlocks[blockKey] ? <Minus size={16} /> : <Plus size={16} />}
            </div>
        </div>
    );

    return (
        <Layout>
            <ConfigProvider theme={antdThemeConfig}>
                <div className={`min-h-screen pb-10 transition-colors duration-300`}>

                    {/* --- HEADER ACTIONS --- */}
                    <div className="border-b dark:border-gray-700 px-4 md:px-6 py-3 flex flex-wrap justify-between items-center gap-4 sticky top-[64px] bg-white dark:bg-[#1f1f1f] z-10 shadow-sm">
                        <div className="flex sm:items-center gap-4 text-sm sm:flex-row flex-col items-start">
                            <div className="flex sm:items-center gap-4 text-sm items-start">
                                <Link to="/sales-contract" className="flex items-center gap-2">
                                    <LeftOutlined className="w-6 h-6 flex items-center justify-center rounded-full bg-[#3e91ab] hover:bg-[#357f96] text-white" />
                                </Link>
                                <span className="font-medium text-[#3e91ab] border-b-2 border-[#3e91ab] pb-1 cursor-pointer">Chi tiết hợp đồng</span>
                            </div>
                            <span className="text-gray-500 dark:text-gray-400 pb-1 font-medium">082026/HDMH/1</span>
                        </div>

                        <div className='overflow-x-auto no-scrollbar'>
                            <div className="flex items-center gap-4 xl:gap-5 w-full xl:w-auto ml-auto min-w-max text-000000e0">
                                <button onClick={() => setIsConfirmModalOpen(true)} className="text-sm flex items-center gap-1.5 hover:text-[#3e91ab]"><CheckSquare size={14} /> Hoàn duyệt</button>
                                <button onClick={() => setIsUpdateDetailModalOpen(true)} className="text-sm flex items-center gap-1.5 hover:text-[#3e91ab]"><Edit3 size={14} /> Cập nhật</button>
                                <button className="flex items-center gap-1.5 hover:text-[#3e91ab] text-sm"><ActivitySquare size={14} /> Tình trạng</button>
                                <button onClick={() => setIsAssigneeModalOpen(true)} className="flex items-center gap-1.5 hover:text-[#3e91ab] text-sm"><User size={14} /> Phụ trách</button>
                            </div>
                        </div>
                    </div>

                    {/* --- MAIN LAYOUT --- */}
                    <div className="mx-auto mt-4 grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">

                        {/* CỘT TRÁI CÓ TABS CHÍNH */}
                        <div className="overflow-hidden">
                            <div className="flex flex-col gap-6 pt-0">

                                {/* 1. THÔNG TIN CHUNG */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Thông tin chung" blockKey="general" />
                                    {expandedBlocks.general && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-4 md:gap-x-8 text-sm animate-in fade-in duration-300 mt-2">
                                            <div>
                                                <p className="text-gray-500 dark:text-gray-400 mb-1">Mã hợp đồng</p>
                                                <p className="font-medium text-gray-900 dark:text-gray-200">082026/HDMH/1</p>
                                            </div>
                                            <div>
                                                <p className="text-gray-500 dark:text-gray-400 mb-1">Nhà cung cấp</p>
                                                <p className="font-medium text-[#3e91ab] cursor-pointer">Công Ty TNHH Thương Mại Dịch Vụ Hoàng Thủy</p>
                                            </div>
                                            <div>
                                                <p className="text-gray-500 dark:text-gray-400 mb-1">Tên hợp đồng</p>
                                                <p className="font-medium text-gray-900 dark:text-gray-200">Hợp đồng nguyên tắc - Mua sản phẩm/dịch vụ</p>
                                            </div>
                                            <div>
                                                <p className="text-gray-500 dark:text-gray-400 mb-1">Kiểu hợp đồng</p>
                                                <p className="font-medium text-gray-900 dark:text-gray-200">Hợp đồng mới</p>
                                            </div>
                                            <div>
                                                <p className="text-gray-500 dark:text-gray-400 mb-1">Ngày ký</p>
                                                <p className="font-medium text-gray-900 dark:text-gray-200">22/08/2026</p>
                                            </div>
                                            <div>
                                                <p className="text-gray-500 dark:text-gray-400 mb-1">Loại hợp đồng</p>
                                                <p className="font-medium text-gray-900 dark:text-gray-200">Hợp đồng mua</p>
                                            </div>
                                            <div>
                                                <p className="text-gray-500 dark:text-gray-400 mb-1">Hiệu lực hợp đồng</p>
                                                <p className="font-medium text-gray-900 dark:text-gray-200">22/08/2026 - 22/08/2028</p>
                                            </div>
                                            <div>
                                                <p className="text-gray-500 dark:text-gray-400 mb-1">Giá trị hợp đồng</p>
                                                <p className="font-medium text-gray-900 dark:text-gray-200">1.093.180.000 ₫</p>
                                            </div>
                                            <div>
                                                <p className="text-gray-500 dark:text-gray-400 mb-1">Địa chỉ</p>
                                                <p className="font-medium text-gray-900 dark:text-gray-200">0 ₫</p>
                                            </div>
                                            <div>
                                                <p className="text-gray-500 dark:text-gray-400 mb-1">Đã thu</p>
                                                <p className="font-medium text-gray-900 dark:text-gray-200">0 ₫</p>
                                            </div>

                                            <div>
                                                <p className="text-gray-500 dark:text-gray-400 mb-1">Trạng thái</p>
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400">Đã duyệt</span>
                                            </div>
                                            <div>
                                                <p className="text-gray-500 dark:text-gray-400 mb-1">Người theo dõi</p>
                                                <div className="flex items-center gap-2">
                                                    <Avatar size={24} style={{ backgroundColor: '#84cc16' }} className="text-xs">H</Avatar> <span className="text-xs font-medium dark:text-gray-200">Mr. Huấn</span>
                                                    <Avatar size={24} style={{ backgroundColor: '#3e91ab' }} className="text-xs">A</Avatar> <span className="text-xs font-medium dark:text-gray-200">Lê Hồng Anh</span>
                                                    <Avatar size={24} className="bg-gray-200 text-gray-500 text-xs font-medium">35</Avatar>
                                                </div>
                                            </div>

                                            <div>
                                                <p className="text-gray-500 dark:text-gray-400 mb-1">Bước duyệt</p>
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">Admin hệ thống(x) -</span>
                                            </div>
                                            <div>
                                                <p className="text-gray-500 dark:text-gray-400 mb-1">Người duyệt</p>
                                                <div className="flex items-center gap-2">
                                                    <Avatar size={24} style={{ backgroundColor: '#84cc16' }} className="text-xs">H</Avatar> <span className="text-xs font-medium dark:text-gray-200">Mr. Huấn</span>
                                                </div>
                                            </div>

                                            <div className="col-span-1 md:col-span-2">
                                                <p className="text-gray-500 dark:text-gray-400 mb-1">Mô tả</p>
                                                <p className="text-gray-900 dark:text-gray-200">đfd</p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* 2. CHI TIẾT HỢP ĐỒNG (SẢN PHẨM) */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Chi tiết hợp đồng" blockKey="contractDetail" />
                                    {expandedBlocks.contractDetail && (
                                        <div className="animate-in fade-in duration-300">
                                            <p className="font-medium text-gray-700 dark:text-gray-300 mb-3 text-sm">Danh sách sản phẩm</p>
                                            <div className="overflow-x-auto">
                                                <Table
                                                    columns={productColumns}
                                                    dataSource={productData}
                                                    rowSelection={{ type: 'checkbox' }}
                                                    pagination={false}
                                                    size="small"
                                                    className="custom-ant-table"
                                                    scroll={{ x: "max-content" }}
                                                    summary={() => (
                                                        <>
                                                            <Table.Summary.Row className="bg-gray-50 dark:bg-gray-700/30 text-gray-900 dark:text-gray-200">
                                                                <Table.Summary.Cell index={0} colSpan={6} align="right" className="font-medium pr-4 whitespace-nowrap">Tổng tiền</Table.Summary.Cell>
                                                                <Table.Summary.Cell index={1} align="right" className="font-medium whitespace-nowrap">1.002.000.000 ₫</Table.Summary.Cell>
                                                            </Table.Summary.Row>
                                                            <Table.Summary.Row className="bg-gray-50 dark:bg-gray-700/30 text-gray-900 dark:text-gray-200">
                                                                <Table.Summary.Cell index={0} colSpan={6} align="right" className="font-medium pr-4 whitespace-nowrap">Chiết khấu trên hợp đồng (%)</Table.Summary.Cell>
                                                                <Table.Summary.Cell index={1} align="right" className="whitespace-nowrap">1 %</Table.Summary.Cell>
                                                            </Table.Summary.Row>
                                                            <Table.Summary.Row className="bg-gray-50 dark:bg-gray-700/30 text-gray-900 dark:text-gray-200">
                                                                <Table.Summary.Cell index={0} colSpan={6} align="right" className="font-medium pr-4 whitespace-nowrap">Chiết khấu trên cả hợp đồng</Table.Summary.Cell>
                                                                <Table.Summary.Cell index={1} align="right" className="whitespace-nowrap">10.020.000 ₫</Table.Summary.Cell>
                                                            </Table.Summary.Row>
                                                            <Table.Summary.Row className="bg-gray-50 dark:bg-gray-700/30 text-gray-900 dark:text-gray-200">
                                                                <Table.Summary.Cell index={0} colSpan={6} align="right" className="font-medium pr-4 whitespace-nowrap">Thuế trên cả hợp đồng</Table.Summary.Cell>
                                                                <Table.Summary.Cell index={1} align="right" className="whitespace-nowrap">100.200.000 ₫</Table.Summary.Cell>
                                                            </Table.Summary.Row>
                                                            <Table.Summary.Row className="bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 text-sm font-semibold">
                                                                <Table.Summary.Cell index={0} colSpan={6} align="right" className="pr-4 whitespace-nowrap">Tổng tiền sau thuế</Table.Summary.Cell>
                                                                <Table.Summary.Cell index={1} align="right" className="whitespace-nowrap">1.093.180.000 ₫</Table.Summary.Cell>
                                                            </Table.Summary.Row>
                                                        </>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* 3. ĐIỀU KHOẢN THANH TOÁN */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Điều khoản thanh toán" blockKey="paymentTerm" />
                                    {expandedBlocks.paymentTerm && (
                                        <div className="overflow-x-auto animate-in fade-in duration-300">
                                            <Table columns={paymentColumns} dataSource={paymentData} rowSelection={{ type: 'checkbox' }} pagination={false}
                                                className="custom-ant-table"
                                                scroll={{ x: "max-content" }}
                                                size="small" />
                                        </div>
                                    )}
                                </div>

                            </div>
                        </div>

                        {/* CỘT PHẢI (THẢO LUẬN / LỊCH SỬ) - Giữ nguyên logic cũ */}
                        <div className="flex flex-col gap-4 h-full">
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 flex flex-col flex-1 min-h-[600px]">
                                <Tabs
                                    defaultActiveKey="2"
                                    className="px-4 pt-2 flex-1 flex flex-col"
                                    items={[
                                        {
                                            key: '1',
                                            label: 'Thảo luận',
                                            children: (
                                                <div className="flex flex-col h-full mt-2">
                                                    <div className="flex-1 overflow-y-auto mb-4 pr-2 max-h-[500px]">
                                                        {comments.map(comment => (
                                                            <div key={comment.id} className="flex gap-3 mb-6">
                                                                <Avatar style={{ backgroundColor: '#84cc16' }}>{comment.author.charAt(0)}</Avatar>
                                                                <div className="flex-1">
                                                                    <div className="flex justify-between items-start">
                                                                        <div className="w-full">
                                                                            <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm">{comment.author}</span>
                                                                            {editingId === comment.id ? (
                                                                                <div className="mt-1 flex gap-2 w-full max-w-sm">
                                                                                    <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} onPressEnter={() => saveEdit(comment.id)} size="small" autoFocus />
                                                                                    <Button size="small" type="primary" onClick={() => saveEdit(comment.id)} className="bg-[#3e91ab]">Lưu</Button>
                                                                                    <Button size="small" onClick={() => setEditingId(null)}>Hủy</Button>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="bg-gray-100 dark:bg-gray-700/50 p-3 rounded-lg rounded-tl-none mt-1 text-sm text-gray-800 dark:text-gray-200 border dark:border-gray-700 w-fit w-full">
                                                                                    {comment.text}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <Dropdown menu={{ items: [{ key: 'edit', label: 'Sửa', onClick: () => startEdit(comment.id, comment.text) }, { key: 'delete', label: 'Xóa', danger: true, onClick: () => handleDeleteComment(comment.id) }] }} trigger={['click']} placement="bottomRight">
                                                                            <MoreVertical size={16} className="text-gray-400 cursor-pointer hover:text-gray-600 ml-2 mt-1" />
                                                                        </Dropdown>
                                                                    </div>
                                                                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                                                                        <span>{comment.time}</span><span className="cursor-pointer hover:text-[#3e91ab]">Thích</span><span className="cursor-pointer hover:text-[#3e91ab]">Trả lời</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="border dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700/30 shrink-0 mb-4">
                                                        <Input bordered={false} placeholder="Viết thảo luận..." className="mb-2 dark:text-gray-200" value={inputValue} onChange={(e) => setInputValue(e.target.value)} onPressEnter={handleSendComment} />
                                                        <div className="flex justify-between items-center text-gray-400">
                                                            <div className="flex gap-3"><Paperclip size={16} className="cursor-pointer hover:text-[#3e91ab]" /><Smile size={16} className="cursor-pointer hover:text-[#3e91ab]" /></div>
                                                            <Send size={16} className="cursor-pointer text-[#3e91ab]" onClick={handleSendComment} />
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        },
                                        {
                                            key: '2',
                                            label: 'Lịch sử hoạt động',
                                            children: (
                                                <div className="mt-4">
                                                    <div className="relative pl-6 border-l border-gray-200 dark:border-gray-600 ml-4 pb-6">
                                                        <div className="absolute -left-[16px] top-0 bg-white dark:bg-gray-800 rounded-full p-0.5">
                                                            <Avatar style={{ backgroundColor: '#84cc16' }} size={28} className="text-xs">H</Avatar>
                                                        </div>
                                                        <div className="pt-0.5">
                                                            <div className="text-sm dark:text-gray-300">
                                                                <span className="font-semibold text-gray-800 dark:text-gray-200">Mr. Huấn</span> đã duyệt
                                                            </div>
                                                            <div className="text-xs text-gray-400 mb-2 mt-0.5">11:42 ngày 22/08/2026</div>
                                                            <div className="bg-[#f8fafc] dark:bg-gray-700/30 p-3 rounded-md text-sm text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                                                                Bước: Admin hệ thống duyệt
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="relative pl-6 border-l border-gray-200 dark:border-gray-600 ml-4 pb-6">
                                                        <div className="absolute -left-[16px] top-0 bg-white dark:bg-gray-800 rounded-full p-0.5">
                                                            <Avatar style={{ backgroundColor: '#84cc16' }} size={28} className="text-xs">H</Avatar>
                                                        </div>
                                                        <div className="pt-0.5">
                                                            <div className="text-sm dark:text-gray-300">
                                                                <span className="font-semibold text-gray-800 dark:text-gray-200">Mr. Huấn</span> đã tạo hợp đồng
                                                            </div>
                                                            <div className="text-xs text-gray-400 mb-2 mt-0.5">10:49 ngày 22/08/2026</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        }
                                    ]}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ================= MODALS MỚI THEO THIẾT KẾ ================= */}

                    {/* 1. MODAL: Xác nhận Hoàn duyệt */}
                    <Modal
                        title={<span className="dark:text-white">Xác nhận</span>}
                        open={isConfirmModalOpen}
                        onCancel={() => setIsConfirmModalOpen(false)}
                        footer={[
                            <Button key="submit" type="primary" style={{ backgroundColor: '#3e91ab', borderColor: '#3e91ab' }} onClick={() => setIsConfirmModalOpen(false)}>ĐỒNG Ý</Button>,
                            <Button key="back" onClick={() => setIsConfirmModalOpen(false)}>HỦY BỎ</Button>,
                        ]}
                        width={400}
                    >
                        <p className="py-4 text-gray-600 dark:text-gray-300">Bạn có chắc muốn hoàn duyệt không?</p>
                    </Modal>

                    {/* 2. MODAL: Duyệt Hợp đồng mua */}
                    <Modal
                        title={<span className="dark:text-white">Duyệt Hợp đồng mua</span>}
                        open={isApproveModalOpen} // Bạn có thể gắn sự kiện mở modal này vào nút duyệt nếu cần
                        onCancel={() => setIsApproveModalOpen(false)}
                        footer={[
                            <Button key="submit" type="primary" style={{ backgroundColor: '#3e91ab', borderColor: '#3e91ab' }} onClick={() => setIsApproveModalOpen(false)}>DUYỆT</Button>,
                            <Button key="back" onClick={() => setIsApproveModalOpen(false)}>KHÔNG CẦN DUYỆT</Button>,
                        ]}
                    >
                        <div className="py-4 text-gray-600 dark:text-gray-300">
                            <p className="mb-2">Duyệt Hợp đồng mua đã chọn</p>
                            <Input placeholder="Thêm ý kiến hoặc để trống" className="w-full border-orange-400 focus:border-[#3e91ab] focus:ring-orange-500" />
                        </div>
                    </Modal>

                    {/* 3. MODAL: Cập nhật chi tiết hợp đồng (Sản phẩm) */}
                    <Modal
                        title={<span className="text-gray-800 font-medium">Cập nhật chi tiết hợp đồng</span>}
                        open={isUpdateDetailModalOpen}
                        onCancel={() => setIsUpdateDetailModalOpen(false)}
                        width={1200}
                        style={{ top: 20 }} // Đẩy modal lên chút để tối ưu không gian màn hình nhỏ
                        footer={[
                            <Button key="back" onClick={() => setIsUpdateDetailModalOpen(false)}>
                                HỦY BỎ
                            </Button>,
                            <Button
                                key="submit"
                                type="primary"
                                style={{ backgroundColor: '#3e91ab', borderColor: '#3e91ab' }}
                                onClick={() => setIsUpdateDetailModalOpen(false)}
                            >
                                CẬP NHẬT
                            </Button>,
                        ]}
                    >
                        <div className="mt-4 pb-4">
                            {/* Title Danh sách sản phẩm */}
                            <div className="flex items-center gap-2 text-[#3e91ab] font-medium mb-4">
                                <ChevronDown size={16} /> Danh sách sản phẩm
                            </div>

                            {/* WRAPPER ĐỂ TẠO SCROLL NGANG TRÊN MOBILE */}
                            <div className="overflow-x-auto pb-2">
                                <div className="min-w-[1000px]">
                                    {/* Header Row */}
                                    <div className="grid grid-cols-[40px_3fr_1fr_1.5fr_1fr_80px_1.5fr_1.5fr_30px] gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 px-1 mb-2 items-center">
                                        <span>STT</span>
                                        <span>Sản phẩm, dịch vụ <span className="text-red-500">*</span></span>
                                        <span>Đơn vị</span>
                                        <span>Giá mua</span>
                                        <span>Số lượng <span className="text-red-500">*</span></span>
                                        <span>Ck (%)</span>
                                        <span>Tiền chiết khấu</span>
                                        <span>Giá trị hợp đồng <span className="text-red-500">*</span></span>
                                        <span className="w-6"></span>
                                    </div>

                                    {/* Dynamic Product Rows */}
                                    {modalProducts.map((item, index) => (
                                        <div key={item.id} className="grid grid-cols-[40px_3fr_1fr_1.5fr_1fr_80px_1.5fr_1.5fr_30px] gap-2 mb-3 items-center">
                                            {/* Checkbox Placeholder / STT */}
                                            <div className="w-8 h-8 border rounded flex items-center justify-center bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-xs text-gray-500">
                                                {index + 1}
                                            </div>

                                            <Select
                                                value={item.product}
                                                options={productOptions}
                                                className="w-full"
                                                onChange={(val) => handleChangeModalProduct(item.id, val)}
                                            />

                                            <Input
                                                value={item.unit}
                                                onChange={(e) => handleUpdateProductField(item.id, 'unit', e.target.value)}
                                            />
                                            <Input
                                                value={item.price ? formatMoney(item.price) : ''}
                                                className="text-right"
                                                onChange={(e) => handleUpdateProductField(item.id, 'price', e.target.value)}
                                            />
                                            <Input
                                                value={item.qty}
                                                className="text-right"
                                                onChange={(e) => handleUpdateProductField(item.id, 'qty', e.target.value)}
                                            />
                                            <Select
                                                value={item.discountType || '%'}
                                                options={[{ value: '%', label: '%' }]}
                                                onChange={(val) => handleUpdateProductField(item.id, 'discountType', val)}
                                            />
                                            <Input
                                                value={item.discountVal || '0'}
                                                className="text-right"
                                                onChange={(e) => handleUpdateProductField(item.id, 'discountVal', e.target.value)}
                                            />
                                            <Input
                                                value={item.total ? formatMoney(item.total) : '0'}
                                                className="text-right bg-gray-50 text-[#3e91ab] font-medium"
                                                readOnly
                                            />

                                            <X
                                                size={18}
                                                className="text-gray-400 hover:text-red-500 cursor-pointer ml-1 shrink-0"
                                                onClick={() => handleRemoveModalProduct(item.id)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Add Product Button */}
                            <button
                                onClick={handleAddModalProduct}
                                className="flex items-center justify-center w-6 h-6 rounded-full border border-[#3e91ab] text-[#3e91ab] hover:bg-[#3e91ab] hover:text-white transition-colors mt-2 mb-6"
                            >
                                <Plus size={14} />
                            </button>

                            {/* Summary Form (Đã sửa lại để Responsive) */}
                            <div className="flex justify-end mt-4">
                                <div className="w-full md:w-[550px] flex flex-col gap-3 text-sm">

                                    <div className="flex items-center justify-between md:justify-end gap-2 md:gap-3 md:pr-8">
                                        <span className="text-gray-500 text-xs md:text-sm">Chiết khấu trên cả HĐ</span>
                                        <div className="flex gap-2">
                                            <Input defaultValue="1" className="w-[60px] md:w-[80px] text-right" />
                                            <Input defaultValue="10,020,000" className="w-[120px] md:w-[160px] text-right" />
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between md:justify-end gap-2 md:gap-3 md:pr-8">
                                        <span className="text-gray-500 text-xs md:text-sm">Thuế trên cả HĐ</span>
                                        <div className="flex gap-2">
                                            <Input defaultValue="10" className="w-[60px] md:w-[80px] text-right" />
                                            <Input defaultValue="100,200,000" className="w-[120px] md:w-[160px] text-right" />
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between md:justify-end gap-2 md:gap-3 relative md:pr-8">
                                        <span className="text-gray-500 text-xs md:text-sm">Phí tổng đơn hàng</span>
                                        <div className="flex gap-2">
                                            <Select defaultValue="Phi" className="w-[60px] md:w-[80px]" options={[{ value: 'Phi', label: 'Phí' }]} />
                                            <Input defaultValue="1,000,000" className="w-[120px] md:w-[160px] text-right" />
                                        </div>
                                        <button className="absolute right-0 md:right-0 -top-6 md:top-auto text-[#3e91ab] border border-[#3e91ab] rounded-full p-0.5 hover:bg-[#3e91ab] hover:text-white transition-colors">
                                            <Plus size={14} />
                                        </button>
                                    </div>

                                    <div className="flex items-center justify-between md:justify-end gap-2 md:gap-3 md:pr-8 mt-1">
                                        <span className="text-gray-600 font-medium text-xs md:text-sm">Tổng tiền sau thuế</span>
                                        <Input
                                            defaultValue="1,093,180,000"
                                            className="w-[188px] md:w-[248px] text-right bg-gray-100 font-medium"
                                            readOnly
                                        />
                                    </div>

                                </div>
                            </div>
                        </div>
                    </Modal>

                    {/* MODAL: Cập nhật người phụ trách */}
                    <Modal
                        title={<span className="dark:text-white">Cập nhật người phụ trách</span>}
                        open={isAssigneeModalOpen}
                        onCancel={() => setIsAssigneeModalOpen(false)}
                        width={900}
                        style={{ top: 20 }}
                        footer={[
                            <Button key="back" onClick={() => setIsAssigneeModalOpen(false)}>HỦY BỎ</Button>,
                            <Button key="submit" type="primary" style={{ backgroundColor: '#3e91ab' }}>CẬP NHẬT</Button>,
                        ]}
                    >
                        {/* WRAPPER ĐỂ TẠO SCROLL NGANG TRÊN MOBILE */}
                        <div className="overflow-x-auto pb-2 mt-4 mb-2">
                            <div className="min-w-[800px]">
                                {/* Header Row */}
                                <div className="grid grid-cols-[1fr_1fr_1.5fr_1.5fr_1fr_1fr_auto] gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 px-1 mb-2">
                                    <span>Từ ngày *</span>
                                    <span>Đến ngày</span>
                                    <span>Người phụ trách</span>
                                    <span>Phòng ban</span>
                                    <span>Vị trí</span>
                                    <span>Chức vụ</span>
                                    <span className="w-4"></span>
                                </div>

                                {/* Rows */}
                                {modalAssignees.map(item => (
                                    <div key={item.id} className="grid grid-cols-[1fr_1fr_1.5fr_1.5fr_1fr_1fr_auto] gap-2 mb-3 items-center">
                                        <DatePicker format="DD/MM/YYYY" placeholder="Select date" className="w-full" />
                                        <DatePicker format="DD/MM/YYYY" placeholder="Select date" className="w-full" />
                                        <Select value={item.user} onChange={(val) => handleChangeModalUser(item.id, val)} placeholder="Chọn..." options={userOptions} className="w-full" />
                                        <Input value={item.department} readOnly className="bg-gray-50 dark:bg-gray-700/50 pointer-events-none" />
                                        <Input value={item.position} readOnly className="bg-gray-50 dark:bg-gray-700/50 pointer-events-none" />
                                        <Input value={item.role} readOnly className="bg-gray-50 dark:bg-gray-700/50 pointer-events-none" />
                                        <X size={16} className="text-gray-400 hover:text-red-500 cursor-pointer shrink-0 ml-1" onClick={() => handleRemoveModalAssignee(item.id)} />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <button onClick={handleAddModalAssignee} className="flex items-center justify-center w-6 h-6 rounded-full border border-[#3e91ab] text-[#3e91ab] hover:bg-[#3e91ab] hover:text-white mt-2">
                            <Plus size={14} />
                        </button>
                    </Modal>

                </div>
            </ConfigProvider>
        </Layout>
    );
}